import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, ExternalLink, GripVertical, Plus, Save, Trash2 } from 'lucide-react'
import { getSubService, updateSubService } from '../api'
import ImageUploader from '../components/ImageUploader'

const CLASS_IDS = [1, 2, 3, 4, 5, 6]

function reorder(list, from, to) {
  const result = Array.from(list)
  const [removed] = result.splice(from, 1)
  result.splice(to, 0, removed)
  return result
}

// ── Таб Основное ─────────────────────────────────────────────────────────────
function TabBasic({ form, onChange }) {
  return (
    <Stack gap="md" pt="md">
      <ImageUploader
        label="Hero-изображение"
        value={form.heroImage}
        onChange={(v) => onChange('heroImage', v)}
      />
      <TextInput
        label="Название"
        required
        value={form.title}
        onChange={(e) => onChange('title', e.target.value)}
      />
      <Textarea
        label="Описание"
        autosize
        minRows={3}
        value={form.description}
        onChange={(e) => onChange('description', e.target.value)}
      />
      <TextInput
        label="Slug (URL)"
        required
        value={form.slug}
        onChange={(e) => onChange('slug', e.target.value)}
      />
    </Stack>
  )
}

// ── Таб Этапы работ ──────────────────────────────────────────────────────────
function TabStages({ stages, onChange }) {
  const addStage = () =>
    onChange([...stages, { image: '', title: '', description: '', order: stages.length }])

  const updateStage = (i, field, value) =>
    onChange(stages.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)))

  const removeStage = (i) => onChange(stages.filter((_, idx) => idx !== i))

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === source.index) return
    onChange(reorder(stages, source.index, destination.index).map((s, i) => ({ ...s, order: i })))
  }

  return (
    <Stack gap="sm" pt="md">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="stages">
          {(provided) => (
            <Stack gap="sm" ref={provided.innerRef} {...provided.droppableProps}>
              {stages.map((stage, i) => (
                <Draggable key={i} draggableId={`stage-${i}`} index={i}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      withBorder
                      p="sm"
                      style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.85 : 1 }}
                    >
                      <Group gap="xs" align="flex-start" wrap="nowrap">
                        <Box {...provided.dragHandleProps} pt={6} style={{ cursor: 'grab', color: '#adb5bd' }}>
                          <GripVertical size={16} />
                        </Box>
                        <Stack gap="xs" style={{ flex: 1 }}>
                          <ImageUploader
                            label={`Фото этапа ${i + 1}`}
                            value={stage.image}
                            onChange={(v) => updateStage(i, 'image', v)}
                          />
                          <TextInput
                            placeholder="Заголовок"
                            value={stage.title}
                            onChange={(e) => updateStage(i, 'title', e.target.value)}
                          />
                          <Textarea
                            placeholder="Описание"
                            autosize
                            minRows={2}
                            value={stage.description}
                            onChange={(e) => updateStage(i, 'description', e.target.value)}
                          />
                        </Stack>
                        <ActionIcon color="red" variant="light" mt={6} onClick={() => removeStage(i)}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
      <Button variant="light" leftSection={<Plus size={15} />} onClick={addStage}>
        Добавить этап
      </Button>
    </Stack>
  )
}

// ── Таб До/После ─────────────────────────────────────────────────────────────
function TabBeforeAfter({ pairs, onChange }) {
  const addPair = () =>
    onChange([...pairs, { beforeImage: '', afterImage: '', order: pairs.length }])

  const updatePair = (i, field, value) => {
    onChange(pairs.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)))
  }

  const removePair = (i) => onChange(pairs.filter((_, idx) => idx !== i))

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === source.index) return
    onChange(reorder(pairs, source.index, destination.index).map((p, i) => ({ ...p, order: i })))
  }

  return (
    <Stack gap="sm" pt="md">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="before-after">
          {(provided) => (
            <Stack gap="sm" ref={provided.innerRef} {...provided.droppableProps}>
              {pairs.map((pair, i) => (
                <Draggable key={i} draggableId={`pair-${i}`} index={i}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      withBorder
                      p="sm"
                      style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.85 : 1 }}
                    >
                      <Group gap="xs" align="flex-start" wrap="nowrap">
                        <Box {...provided.dragHandleProps} pt={6} style={{ cursor: 'grab', color: '#adb5bd' }}>
                          <GripVertical size={16} />
                        </Box>
                        <SimpleGrid cols={2} style={{ flex: 1 }}>
                          <ImageUploader
                            label="До"
                            value={pair.beforeImage}
                            onChange={(v) => updatePair(i, 'beforeImage', v)}
                          />
                          <ImageUploader
                            label="После"
                            value={pair.afterImage}
                            onChange={(v) => updatePair(i, 'afterImage', v)}
                          />
                        </SimpleGrid>
                        <ActionIcon color="red" variant="light" mt={6} onClick={() => removePair(i)}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
      <Button variant="light" leftSection={<Plus size={15} />} onClick={addPair}>
        Добавить пару фото
      </Button>
    </Stack>
  )
}

// ── Таб FAQ ──────────────────────────────────────────────────────────────────
function TabFaq({ faq, onChange }) {
  const addItem = () =>
    onChange([...faq, { question: '', answer: '', order: faq.length }])

  const updateItem = (i, field, value) =>
    onChange(faq.map((f, idx) => (idx === i ? { ...f, [field]: value } : f)))

  const removeItem = (i) => onChange(faq.filter((_, idx) => idx !== i))

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === source.index) return
    onChange(reorder(faq, source.index, destination.index).map((f, i) => ({ ...f, order: i })))
  }

  return (
    <Stack gap="sm" pt="md">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="faq">
          {(provided) => (
            <Stack gap="sm" ref={provided.innerRef} {...provided.droppableProps}>
              {faq.map((item, i) => (
                <Draggable key={i} draggableId={`faq-${i}`} index={i}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      withBorder
                      p="sm"
                      style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.85 : 1 }}
                    >
                      <Group gap="xs" align="flex-start" wrap="nowrap">
                        <Box {...provided.dragHandleProps} pt={6} style={{ cursor: 'grab', color: '#adb5bd' }}>
                          <GripVertical size={16} />
                        </Box>
                        <Stack gap="xs" style={{ flex: 1 }}>
                          <TextInput
                            placeholder="Вопрос"
                            value={item.question}
                            onChange={(e) => updateItem(i, 'question', e.target.value)}
                          />
                          <Textarea
                            placeholder="Ответ"
                            autosize
                            minRows={2}
                            value={item.answer}
                            onChange={(e) => updateItem(i, 'answer', e.target.value)}
                          />
                        </Stack>
                        <ActionIcon color="red" variant="light" mt={6} onClick={() => removeItem(i)}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
      <Button variant="light" leftSection={<Plus size={15} />} onClick={addItem}>
        Добавить вопрос
      </Button>
    </Stack>
  )
}

// ── Таб Прайс-лист ───────────────────────────────────────────────────────────
function TabPrice({ priceList, onChange }) {
  const addRow = () =>
    onChange([
      ...priceList,
      {
        name: '',
        prices: { 1: null, 2: null, 3: null, 4: null, 5: null, 6: null },
        order: priceList.length,
      },
    ])

  const updateRow = (i, field, value) =>
    onChange(priceList.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)))

  const updatePrice = (i, classId, value) =>
    onChange(
      priceList.map((row, idx) =>
        idx === i ? { ...row, prices: { ...row.prices, [classId]: value } } : row
      )
    )

  const removeRow = (i) => onChange(priceList.filter((_, idx) => idx !== i))

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === source.index) return
    onChange(reorder(priceList, source.index, destination.index).map((r, i) => ({ ...r, order: i })))
  }

  return (
    <Stack gap="sm" pt="md">
      <Box style={{ overflowX: 'auto' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="price-rows" direction="vertical">
            {(provided) => (
              <Table withTableBorder withColumnBorders fz="sm" style={{ minWidth: 700 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={28} />
                    <Table.Th>Позиция</Table.Th>
                    {CLASS_IDS.map((c) => (
                      <Table.Th key={c} ta="center">{c} класс</Table.Th>
                    ))}
                    <Table.Th w={36} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {priceList.map((row, i) => (
                    <Draggable key={i} draggableId={`price-${i}`} index={i}>
                      {(provided, snapshot) => (
                        <Table.Tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            background: snapshot.isDragging ? '#f1f3f5' : undefined,
                          }}
                        >
                          <Table.Td>
                            <Box
                              {...provided.dragHandleProps}
                              style={{ cursor: 'grab', color: '#adb5bd', display: 'flex' }}
                            >
                              <GripVertical size={14} />
                            </Box>
                          </Table.Td>
                          <Table.Td>
                            <TextInput
                              size="xs"
                              placeholder="Название позиции"
                              value={row.name}
                              onChange={(e) => updateRow(i, 'name', e.target.value)}
                              styles={{ input: { minWidth: 160 } }}
                            />
                          </Table.Td>
                          {CLASS_IDS.map((c) => (
                            <Table.Td key={c}>
                              <NumberInput
                                size="xs"
                                placeholder="0"
                                value={row.prices?.[c] ?? ''}
                                onChange={(v) => updatePrice(i, c, v === '' ? null : Number(v))}
                                min={0}
                                step={500}
                                hideControls
                                styles={{ input: { width: 88, textAlign: 'right' } }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Tab') return
                                }}
                              />
                            </Table.Td>
                          ))}
                          <Table.Td>
                            <ActionIcon size="sm" color="red" variant="light" onClick={() => removeRow(i)}>
                              <Trash2 size={12} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Table.Tbody>
              </Table>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <Box>
        <Button variant="light" size="xs" leftSection={<Plus size={14} />} onClick={addRow}>
          Добавить строку
        </Button>
      </Box>
    </Stack>
  )
}

// ── Таб Текстовые блоки ──────────────────────────────────────────────────────
function TabTextBlocks({ textBlocks, onChange }) {
  const addItem = () =>
    onChange([...textBlocks, { heading: '', body: '', order: textBlocks.length }])

  const updateItem = (i, field, value) =>
    onChange(textBlocks.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)))

  const removeItem = (i) => onChange(textBlocks.filter((_, idx) => idx !== i))

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === source.index) return
    onChange(reorder(textBlocks, source.index, destination.index).map((b, i) => ({ ...b, order: i })))
  }

  return (
    <Stack gap="sm" pt="md">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="text-blocks">
          {(provided) => (
            <Stack gap="sm" ref={provided.innerRef} {...provided.droppableProps}>
              {textBlocks.map((item, i) => (
                <Draggable key={i} draggableId={`text-block-${i}`} index={i}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      withBorder
                      p="sm"
                      style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.85 : 1 }}
                    >
                      <Group gap="xs" align="flex-start" wrap="nowrap">
                        <Box {...provided.dragHandleProps} pt={6} style={{ cursor: 'grab', color: '#adb5bd' }}>
                          <GripVertical size={16} />
                        </Box>
                        <Stack gap="xs" style={{ flex: 1 }}>
                          <TextInput
                            placeholder="Заголовок"
                            value={item.heading}
                            onChange={(e) => updateItem(i, 'heading', e.target.value)}
                          />
                          <Textarea
                            placeholder="Текст"
                            autosize
                            minRows={3}
                            value={item.body}
                            onChange={(e) => updateItem(i, 'body', e.target.value)}
                          />
                        </Stack>
                        <ActionIcon color="red" variant="light" mt={6} onClick={() => removeItem(i)}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
      <Button variant="light" leftSection={<Plus size={15} />} onClick={addItem}>
        Добавить блок
      </Button>
    </Stack>
  )
}

// ── Главный компонент ─────────────────────────────────────────────────────────
export default function SubServiceEditor() {
  const { id, subId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [form, setForm] = useState(null)
  const originalRef = useRef(null)

  useEffect(() => {
    getSubService(subId)
      .then((data) => {
        setForm(data)
        originalRef.current = JSON.stringify(data)
        setDirty(false)
      })
      .catch((err) => notifications.show({ color: 'red', message: err.message }))
      .finally(() => setLoading(false))
  }, [subId])

  useEffect(() => {
    if (!form) return
    setDirty(JSON.stringify(form) !== originalRef.current)
  }, [form])

  useEffect(() => {
    const handler = (e) => { if (dirty) e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const handleChange = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }))
  }, [])

  const handleSave = async () => {
    if (!form.title || !form.slug) {
      notifications.show({ color: 'orange', message: 'Название и slug обязательны' })
      return
    }
    setSaving(true)
    try {
      const saved = await updateSubService(subId, form)
      setForm(saved)
      originalRef.current = JSON.stringify(saved)
      setDirty(false)
      notifications.show({ color: 'green', message: 'Сохранено' })
    } catch (err) {
      notifications.show({ color: 'red', title: 'Ошибка', message: err.message })
    } finally {
      setSaving(false)
    }
  }

  if (loading || !form) return <Loader />

  return (
    <Box>
      <Group mb="lg" justify="space-between">
        <Group gap="sm">
          <ActionIcon variant="subtle" onClick={() => navigate(`/admin/services/${id}`)}>
            <ArrowLeft size={18} />
          </ActionIcon>
          <Title order={4}>{form?.title || 'Редактор подуслуги'}</Title>
          {dirty && <Badge color="orange" size="sm">Несохранённые изменения</Badge>}
        </Group>
        <Group gap="sm">
          <Tooltip label="Открыть на сайте">
            <ActionIcon
              variant="light"
              component="a"
              href={`/services/${form?.slug}`}
              target="_blank"
            >
              <ExternalLink size={16} />
            </ActionIcon>
          </Tooltip>
          <Button
            leftSection={<Save size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={!dirty}
          >
            Сохранить
          </Button>
        </Group>
      </Group>

      <Tabs defaultValue="basic">
        <Tabs.List>
          <Tabs.Tab value="basic">Основное</Tabs.Tab>
          <Tabs.Tab value="stages">Этапы работ</Tabs.Tab>
          <Tabs.Tab value="before-after">До / После</Tabs.Tab>
          <Tabs.Tab value="faq">FAQ</Tabs.Tab>
          <Tabs.Tab value="price">Прайс-лист</Tabs.Tab>
          <Tabs.Tab value="text-blocks">Текстовые блоки</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic">
          <TabBasic form={form} onChange={handleChange} />
        </Tabs.Panel>

        <Tabs.Panel value="stages">
          <TabStages
            stages={form?.stages || []}
            onChange={(v) => handleChange('stages', v)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="before-after">
          <TabBeforeAfter
            pairs={form?.beforeAfter || []}
            onChange={(v) => handleChange('beforeAfter', v)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="faq">
          <TabFaq
            faq={form?.faq || []}
            onChange={(v) => handleChange('faq', v)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="price">
          <TabPrice
            priceList={form?.priceList || []}
            onChange={(v) => handleChange('priceList', v)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="text-blocks">
          <TabTextBlocks
            textBlocks={form?.textBlocks || []}
            onChange={(v) => handleChange('textBlocks', v)}
          />
        </Tabs.Panel>
      </Tabs>
    </Box>
  )
}
